import type { PlatformerLevel, Position } from '../../../shared/CharacterTypes';
import type { Teleporter } from '../../../shared/Teleporter';
import { isStandablePlatform } from '../../../shared/logic/PlatformSolidity';
import { GAME_CONSTANTS } from '../../constants/GameConstants';
import { gameLogger } from '../../../utils/GameLogger';

/** Room-local spawn resolution. Construct from the owner's current level per call. */
export class MapSpawnResolver {
  constructor(private readonly currentLevel: PlatformerLevel | null, private readonly teleporters: readonly Teleporter[]) {}


  /**
   * Calculates the final spawn position based on priority rules
   */
  public calculateSpawnPosition(savedPosition: Position, spawnId?: string): Position {
    // 1. Check for Targeted Spawn (highest priority)
    if (spawnId) {
      if (spawnId === 'default') {
        return this.getDefaultSpawnPosition();
      }
      // Resolve from this MapSystem's own level data. TeleporterManager is a
      // legacy process singleton; on the server, multiple rooms can load
      // different maps concurrently and overwrite its active teleporter list.
      // The level is room-local and therefore authoritative for join spawns.
      const target = this.currentLevel?.platforms.find(
        (platform) => platform.id === spawnId,
      );
      if (target) {
        return this.validateOnGround({
          x: target.x + ((target.width || 64) / 2),
          y: target.y + (target.height || 96),
        });
      }

      // Check teleporter manager if present
      const teleporter = this.teleporters.find(
        (t) => t.id === spawnId,
      );
      if (teleporter) {
        return this.validateOnGround({
          x: teleporter.x + ((teleporter.width || 64) / 2),
          y: teleporter.y + (teleporter.height || 96),
        });
      }

      // If hero was already standing near a platform on this map (same-map resume with stale spawnId),
      // keep them on that floor rather than resetting them to default spawn.
      if (this.isPositionNearPlatform(savedPosition)) {
        return savedPosition;
      }

      // Explicit spawnId was requested but not found — fall back to canonical default spawn,
      // not cross-map savedPosition or a random brawl point!
      gameLogger.warn('map', `[MapSystem] Requested spawnId '${spawnId}' not found on level '${this.currentLevel?.id}' — falling back to canonical default`);
      return this.currentLevel?.defaultSpawn
        ? this.validateOnGround(this.currentLevel.defaultSpawn)
        : this.getDefaultSpawnPosition();
    }

    // 2. Check Map Type logic
    const isField = this.currentLevel?.type === 'field';
    if (isField) {
      return this.getDefaultSpawnPosition();
    }

    // 3. Persistence (Towns) — only trust saved position if it's actually
    //    near a platform. Freshly-created characters get a generic (300,300)
    //    from the API, which is in empty space on most maps.
    if (this.isPositionNearPlatform(savedPosition)) {
      return savedPosition;
    }

    // Saved position is in the void — use map's default spawn instead
    gameLogger.warn('map', `[MapSystem] Saved position (${savedPosition.x}, ${savedPosition.y}) is not near any platform — using default spawn`);
    return this.getDefaultSpawnPosition();
  }


  /**
   * Get default spawn position.
   *
   * Resolution priority:
   *   1. `currentLevel.defaultSpawn` if set (single canonical spawn)
   *   2. `currentLevel.spawnPoints[N]` — RANDOM if multiple, [0] if one
   *      (random-pick triggered when len > 1 + no defaultSpawn; brawl
   *      arena uses this so kills don't clump same-spot)
   *   3. GAME_CONSTANTS.DEFAULTS.SPAWN_POS as last resort
   *
   * Single-spawn maps with `defaultSpawn` set are unaffected by the
   * random-pick (same behavior as before). Maps with one spawnPoint
   * and no defaultSpawn also unaffected (single point trivially picked).
   */
  public getDefaultSpawnPosition(): Position {
    let spawn: Position | undefined;

    if (this.currentLevel?.defaultSpawn) {
      spawn = this.currentLevel.defaultSpawn;
    } else if (this.currentLevel?.spawnPoints && this.currentLevel.spawnPoints.length > 0) {
      const points = this.currentLevel.spawnPoints;
      spawn = points.length > 1
        ? points[Math.floor(Math.random() * points.length)]
        : points[0];
    }

    if (!spawn) {
      spawn = { ...GAME_CONSTANTS.DEFAULTS.SPAWN_POS };
    }

    // Per-map opt-in: maps that want raycast-style snap (only snap if a
    // platform is within `maxFallPx`, otherwise leave aerial) set
    // `metadata.spawnSnapMaxFallPx` on the level. Brawl arena uses this
    // so NW/NE corner spawns stay aerial + the player drops in via
    // gravity instead of teleporting to the floor.
    const maxFallPx = (this.currentLevel as any)?.metadata?.spawnSnapMaxFallPx;
    return this.validateOnGround(
      spawn,
      typeof maxFallPx === 'number' ? { maxFallPx } : undefined,
    );
  }


  /**
   * Snaps a position to the nearest ground below it.
   *
   * Default behavior (backward-compatible): scan ALL platforms below the
   * input position at the same X column, snap to the topmost one. No
   * upper bound on how far the snap can teleport the position downward.
   * Used by single-spawn-point maps where every spawn coordinate is
   * authored intentionally near a platform.
   *
   * Raycast behavior (opt-in via `opts.maxFallPx`): only snap if the
   * candidate platform is within `maxFallPx` of the input position. If
   * the closest platform is farther down, leave the position alone (the
   * caller — typically a multi-corner brawl-arena spawn — wants the
   * aerial spawn preserved so the player visibly drops in via gravity
   * rather than teleporting to the floor).
   *
   * Per-map opt-in: set `metadata.spawnSnapMaxFallPx` on the level.
   * `getDefaultSpawnPosition` reads it + passes through.
   */
  public validateOnGround(pos: Position, opts?: { maxFallPx?: number }): Position {
    if (!this.currentLevel) return pos;

    // Only platforms a body can actually stand on are snap candidates. This
    // filtered `rope` alone — it ignored sensors AND the explicit `solid:
    // false` opt-out that the ground check and the server's mob snapper both
    // already honoured, so a spawn could snap onto a DRAWING: on 2026-07-26
    // `/combatlab` spawned the player on its own 2px facing-axis marker at
    // y=619, 41px above the floor the whole map is measured from. Now shares
    // the ground check's predicate — see PlatformSolidity.ts, which also
    // documents why `decorative` still is not implicitly non-standable.
    const platforms = this.currentLevel.platforms.filter((p: any) => isStandablePlatform(p));
    if (platforms.length === 0) return pos;

    const maxFallPx = opts?.maxFallPx ?? Infinity;

    const below = platforms.filter((p: any) =>
      pos.x >= p.x &&
      pos.x <= p.x + p.width &&
      p.y >= pos.y - GAME_CONSTANTS.PHYSICS.GROUND_SNAP_BUFFER &&
      // Raycast guard: skip platforms that are farther below than the
      // caller is willing to let the snap teleport. When maxFallPx is
      // Infinity (default) this gate is a no-op.
      (p.y - pos.y) <= maxFallPx
    );

    if (below.length > 0) {
      below.sort((a: any, b: any) => a.y - b.y);
      const nearest = below[0];
      return {
        x: pos.x,
        y: nearest.y + GAME_CONSTANTS.PHYSICS.GROUND_SNAP_OFFSET_Y
      };
    }

    return pos;
  }


  /**
   * Checks whether a position is within reasonable proximity of any platform.
   * A position is "near" a platform if there's a platform surface within
   * MAX_FALL_DISTANCE pixels directly below it AND the horizontal position
   * is within that platform's x-bounds.
   */
  private isPositionNearPlatform(pos: Position): boolean {
    if (!this.currentLevel) return false;

    const platforms = this.currentLevel.platforms.filter((p: any) => p.type !== 'rope');
    if (platforms.length === 0) return false;

    // How far below the position do we look for a platform? 512px ≈ 16 tiles.
    // If there's no platform within 16 tiles below you, you're in the void.
    const MAX_FALL_DISTANCE = 512;

    const reachable = platforms.some((p: any) =>
      pos.x >= p.x &&
      pos.x <= p.x + p.width &&
      p.y >= pos.y &&
      p.y <= pos.y + MAX_FALL_DISTANCE
    );

    return reachable;
  }
}
